// src/components/Filters/FilterBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSearch,
  toggleStatusFilter,
  togglePriorityFilter,
  toggleAssigneeFilter,
  clearAllFilters,
  selectFilters,
} from '../../store/slices/filtersSlice.js';
import { selectAllAssignees } from '../../store/slices/tasksSlice.js';
import { TASK_STATUS_ORDER, TASK_STATUS_LABELS, TASK_PRIORITY, TASK_PRIORITY_LABELS } from '../../constants/task.js';
import { hasActiveFilters } from '../../utils/taskUtils.js';

function FilterDropdown({ label, options, selected, onToggle, getLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="filter-group" ref={ref}>
      <button
        className={`filter-btn${selected.length ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {label}
        {selected.length > 0 && <span className="active-count">{selected.length}</span>}
        <span style={{ fontSize: 9 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="filter-dropdown" role="listbox" aria-multiselectable="true">
          {options.map((opt) => (
            <div
              key={opt}
              className={`filter-option${selected.includes(opt) ? ' selected' : ''}`}
              onClick={() => onToggle(opt)}
              role="option"
              aria-selected={selected.includes(opt)}
            >
              <div className={`check-box${selected.includes(opt) ? ' checked' : ''}`}>
                {selected.includes(opt) && '✓'}
              </div>
              {getLabel ? getLabel(opt) : opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FilterBar() {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const allAssignees = useSelector(selectAllAssignees);
  const active = hasActiveFilters(filters);

  return (
    <div className="filter-bar" role="search">
      <div className="search-wrap">
        <span className="search-icon">🔍</span>
        <input
          className="search-input"
          type="search"
          placeholder="Search tasks..."
          value={filters.search}
          onChange={(e) => dispatch(setSearch(e.target.value))}
          aria-label="Search tasks by title"
        />
      </div>

      <FilterDropdown
        label="Status"
        options={TASK_STATUS_ORDER}
        selected={filters.status}
        onToggle={(v) => dispatch(toggleStatusFilter(v))}
        getLabel={(v) => TASK_STATUS_LABELS[v]}
      />

      <FilterDropdown
        label="Priority"
        options={Object.values(TASK_PRIORITY)}
        selected={filters.priority}
        onToggle={(v) => dispatch(togglePriorityFilter(v))}
        getLabel={(v) => TASK_PRIORITY_LABELS[v]}
      />

      <FilterDropdown
        label="Assignee"
        options={allAssignees}
        selected={filters.assignee}
        onToggle={(v) => dispatch(toggleAssigneeFilter(v))}
      />

      {active && (
        <button className="btn btn-ghost btn-sm" onClick={() => dispatch(clearAllFilters())}>
          × Clear filters
        </button>
      )}
    </div>
  );
}
